import { useEffect } from 'react';
import { useLocation } from '@reach/router';

/**
 * Passed by search result links as navigation state, so it stays out of the page address
 * (and analytics): <Link to={path} state={{ searchHighlight }}>
 */
export interface SearchHighlight {
	// The snippet's sentence
	sentence: string;
	// The first matched word, used when the sentence isn't found on the page
	word: string;
}

// Styled with ::highlight(search-result) in GlobalStyles
const HIGHLIGHT_NAME = 'search-result';

// CSS Custom Highlight API, not yet in this TypeScript version's DOM types
declare const Highlight: { new (...ranges: Range[]): unknown };
const highlights = (): Map<string, unknown> | undefined => (window.CSS as any)?.highlights;

// Only letters and digits are compared: spacing, punctuation and soft hyphens differ between
// the indexed text and the page (e.g. full stops added after headings when indexing)
const WORD_CHAR = /[\p{L}\p{N}]/u;

interface CharPosition {
	node: Text;
	start: number;
	end: number;
}

// Text that isn't displayed, e.g. a closed annotation popup, can't be scrolled to.
// Button labels aren't indexed, e.g. "Kaart" between a heading and its text, so they'd break up the sentence
function isVisibleText(node: Node) {
	const parent = node.parentElement;
	return parent && parent.getClientRects().length > 0 && !parent.closest('button')
		? NodeFilter.FILTER_ACCEPT
		: NodeFilter.FILTER_REJECT;
}

// A sentence end followed by the start of the next sentence, as in lib/search.ts
const SENTENCE_BREAK = /[.!?…]\s+(?=[„"«(]?\p{Lu})/u;

// The sentence from the matched word on, e.g. just the heading "Kliimaneutraalne linn." when the snippet's
// sentence starts with a button label before it, or continues into lazy-loaded text that isn't on the page yet
function sentenceFromWord(sentence: string, word: string) {
	const fromWord = sentence.slice(sentence.indexOf(word));
	const end = fromWord.search(SENTENCE_BREAK);
	return end === -1 ? fromWord : fromWord.slice(0, end + 1);
}

function normalize(text: string) {
	return Array.from(text)
		.filter((char) => WORD_CHAR.test(char))
		.join('')
		.toLowerCase();
}

function findTextRange(root: Element, text: string): Range | null {
	const target = normalize(text);
	if (!target) return null;

	let normalized = '';
	const positions: CharPosition[] = [];
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: isVisibleText });
	for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
		let offset = 0;
		for (const char of node.data) {
			if (WORD_CHAR.test(char)) {
				// One position per character of the lowercased text, which can be longer than the original
				for (const lower of char.toLowerCase()) {
					normalized += lower;
					positions.push({ node, start: offset, end: offset + char.length });
				}
			}
			offset += char.length;
		}
	}

	const index = normalized.indexOf(target);
	if (index === -1) return null;
	const first = positions[index];
	const last = positions[index + target.length - 1];
	const range = document.createRange();
	range.setStart(first.node, first.start);
	range.setEnd(last.node, last.end);
	return range;
}

// Lazy-loaded parts of the page (e.g. Hero) change its height after the first render,
// so the found text is only scrolled to once the height has stayed the same for a few frames
function whenLayoutStable(callback: () => void) {
	let frame = 0;
	let prevHeight = -1;
	let stableFrames = 0;
	let attempts = 0;
	const check = () => {
		const height = document.documentElement.scrollHeight;
		stableFrames = height === prevHeight ? stableFrames + 1 : 0;
		prevHeight = height;
		if (stableFrames >= 3 || attempts++ > 60) callback();
		else frame = requestAnimationFrame(check);
	};
	frame = requestAnimationFrame(check);
	return () => cancelAnimationFrame(frame);
}

/** Highlights and scrolls to the sentence a search result linked to */
export function useSearchHighlight() {
	const location = useLocation();
	const highlight: SearchHighlight | undefined = (location.state as any)?.searchHighlight;

	useEffect(() => {
		if (!highlight) return;
		const { sentence, word } = highlight;

		const cancel = whenLayoutStable(() => {
			const root = document.querySelector('main');
			if (!root) return;
			// The sentence can be missing from the page when indexing changed it too much, e.g. a footnote number.
			// The word alone is the last resort, as it scrolls to the word's first use on the page
			const range =
				findTextRange(root, sentence) ??
				findTextRange(root, sentenceFromWord(sentence, word)) ??
				findTextRange(root, word);
			if (!range) return;

			if (typeof Highlight !== 'undefined') highlights()?.set(HIGHLIGHT_NAME, new Highlight(range));
			const { top } = range.getBoundingClientRect();
			window.scrollTo({ top: window.scrollY + top - window.innerHeight / 3, behavior: 'smooth' });
		});

		return () => {
			cancel();
			highlights()?.delete(HIGHLIGHT_NAME);
		};
		// key: every navigation, also to the same page from another result
	}, [location.key, location.pathname]);
}
